// src/admin/pages/AdminUnblock.jsx
// Live list of every conversation currently sitting in state =
// 'blocked_bot_loop' (bot paused itself after unusually high message
// volume -- either one account messaging a bot rapidly, or two bots
// stuck replying to each other). Backed by a partial index scoped to
// just this state, so this stays cheap regardless of how big
// `conversations` grows -- see GET admin-blocked-accounts's sticky note.
//
// Deliberately does NOT try to show *why* a given account tripped the
// block (single-side burst vs. two-way pair burst) -- the underlying
// burst_count/burst_window_start columns get overwritten by whatever
// the *current* message window looks like even while an account stays
// blocked, so a live snapshot can be flat-out wrong, not just
// incomplete. times_blocked_bot_loop / last_blocked_bot_loop_at are the
// only numbers here that are actually reliable after the fact.
//
// This intentionally is NOT folded into MerchantDetailSheet.jsx --
// that sheet is deliberately kept free of individual customer/order
// data. Blocking is inherently customer(or other-merchant)-identifying,
// and applies platform-wide (a "customer" here can itself be another
// merchant's bot account), so it gets its own page instead.

import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Unlock, RefreshCcw, Instagram, AlertTriangle } from "lucide-react";
import RequireAdmin from "@/admin/components/RequireAdmin";
import AdminLayout from "@/admin/components/AdminLayout";
import adminApi from "@/admin/lib/adminApi";

function timeAgo(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function AccountRow({ account, onUnblocked }) {
  const [unblocking, setUnblocking] = useState(false);

  const handleUnblock = async () => {
    if (unblocking) return;
    setUnblocking(true);
    try {
      await adminApi.post("/admin/unblock-account", {
        conversation_id: account.conversation_id,
      });
      toast.success(`Unblocked ${account.display_name} — they can message the bot again.`);
      onUnblocked(account.conversation_id);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Already unblocked (race with another admin, or resolved on its
        // own) -- still remove it from the list rather than leaving a
        // stale row the button can't act on.
        toast.info("Already unblocked.");
        onUnblocked(account.conversation_id);
      } else {
        toast.error("Couldn't unblock — try again.");
      }
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <tr className="border-b border-ink/5" data-testid={`blocked-row-${account.conversation_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-sunset-gradient text-white">
            <Instagram size={12} />
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold">{account.display_name}</p>
            <p className="font-mono text-xs text-ink/40">{account.customer_ig_id}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-ink/70">
        {account.business_name}
        {account.business_ig_username && (
          <span className="block text-xs text-ink/40">@{account.business_ig_username}</span>
        )}
      </td>
      <td className="px-4 py-3 text-ink/60">{timeAgo(account.updated_at) || "—"}</td>
      <td className="px-4 py-3">
        {account.times_blocked_bot_loop > 1 ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-peach/60 px-2 py-0.5 text-xs font-semibold text-ink"
            title={`Blocked ${account.times_blocked_bot_loop} times total, last on ${
              account.last_blocked_bot_loop_at
                ? new Date(account.last_blocked_bot_loop_at).toLocaleString()
                : "unknown"
            }`}
          >
            <AlertTriangle size={11} /> {account.times_blocked_bot_loop}x
          </span>
        ) : (
          <span className="text-xs text-ink/40">1st time</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={handleUnblock}
          disabled={unblocking}
          data-testid={`unblock-btn-${account.conversation_id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs font-bold text-cream transition hover:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {unblocking ? <Loader2 size={13} className="animate-spin" /> : <Unlock size={13} />}
          {unblocking ? "Unblocking…" : "Unblock"}
        </button>
      </td>
    </tr>
  );
}

function AdminUnblockInner() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.get("/admin/blocked-accounts");
      setAccounts(data.accounts || []);
    } catch {
      setError("Couldn't load blocked accounts.");
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnblocked = (conversationId) => {
    setAccounts((prev) => prev.filter((a) => a.conversation_id !== conversationId));
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl tracking-tighter">Blocked accounts</h1>
            <p className="mt-1 text-sm text-ink/60">
              IG accounts currently paused for unusually high message volume — either a
              customer messaging a bot rapidly, or two merchant bots stuck replying to each
              other.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex flex-none items-center gap-1.5 rounded-xl border border-ink/15 px-3 py-2 text-sm font-semibold transition hover:bg-cream disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          {loading ? (
            <div className="grid place-items-center p-10">
              <Loader2 className="h-5 w-5 animate-spin text-ink/40" />
            </div>
          ) : error ? (
            <p className="p-8 text-center text-sm text-ink/50">{error}</p>
          ) : accounts.length === 0 ? (
            <p className="p-8 text-center text-sm text-ink/50">
              Nothing blocked right now — all clear.
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-ink/10 bg-cream/60 text-xs font-bold uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Merchant</th>
                  <th className="px-4 py-3">Blocked</th>
                  <th className="px-4 py-3">History</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <AccountRow key={a.conversation_id} account={a} onUnblocked={handleUnblocked} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && accounts.length === 100 && (
          <p className="mt-3 text-xs text-ink/40">
            Showing the 100 most recently blocked — refresh after clearing some to see more.
          </p>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminUnblock() {
  return (
    <RequireAdmin>
      <AdminUnblockInner />
    </RequireAdmin>
  );
}
